import React, { useEffect, useMemo, useState } from 'react';
import { DocumentStatus } from '@/types';
import UnitDocumentFilterableList from './UnitDocumentFilterableList';
import { ResidentialUnitDocumentSearchOptions, searchResidentialUnitDocuments } from '@/services/documents';
import { Trans } from '@lingui/react/macro';
import { useEffectSkipFirst } from '@/utils/hooks';
import { faFileArchive } from '@fortawesome/pro-light-svg-icons';
import DocumentZipDownloadDialog from './DocumentZipCreateDialog';
import DocumentsFilter from './DocumentsFilter';
import { AlertBanner, Button, ButtonBar, SlideToggle, Tabs } from '@ygtechnologies/rubus-ui';

const ALL_STATUS = 'all';
import { useHistory, useLocation } from 'react-router';
import { Link } from 'react-router-dom';

interface UnitDocumentsProps {
    projectId: number;
}

function getDocumentsFilter(status: DocumentStatus | null): ResidentialUnitDocumentSearchOptions {
    return {
        status: status ? [status] : undefined,
        excludeProjectDocuments: true,
        includeArchived: false,
        categories: [],
        includeUnpublishedVersionsCount: true,
    };
}

const UnitDocuments: React.FC<UnitDocumentsProps> = ({ projectId }) => {
    const history = useHistory();
    const location = useLocation();
    const urlParams = new URLSearchParams(location.search);
    const statusFromUrl = urlParams.get('status') as DocumentStatus | null;

    const [selectedStatus, setSelectedStatus] = useState<DocumentStatus | null>(statusFromUrl);
    const [pendingCount, setPendingCount] = useState<number>(0);

    const defaultFilter = useMemo(() => {
        return getDocumentsFilter(selectedStatus);
    }, [selectedStatus]);

    const [filters, setFilters] = useState(defaultFilter);

    useEffectSkipFirst(() => {
        setFilters(defaultFilter);
    }, [defaultFilter]);

    const [documentZipDialogIsOpen, setDocumentZipDialogIsOpen] = useState(false);

    // Update URL when status changes
    const handleStatusChange = (status: DocumentStatus | null) => {
        setSelectedStatus(status);
        const params = new URLSearchParams(location.search);
        if (status) {
            params.set('status', status.toLowerCase());
        } else {
            params.delete('status');
        }
        history.push({ search: params.toString() });
    };

    // Fetch pending document count
    useEffect(() => {
        const fetchPendingCount = async () => {
            const pendingDocs = await searchResidentialUnitDocuments({
                projectId,
                excludeProjectDocuments: true,
                includeArchived: filters.includeArchived,
                categories: filters.categories || [],
                includeUnpublishedVersionsCount: true,
                status: [DocumentStatus.Pending],
            });

            setPendingCount(pendingDocs.length);
        };

        fetchPendingCount();
    }, [projectId, filters.includeArchived, filters.categories]);

    return (
        <>
            <ButtonBar sticky margins="none" stickyClassName="stickyBleed">
                <Tabs
                    activeId={selectedStatus ?? ALL_STATUS}
                    onChange={(id) => handleStatusChange(id === ALL_STATUS ? null : (id as DocumentStatus))}
                    variant="pill"
                    size="sm">
                    <Tabs.Tab id={ALL_STATUS} label={<Trans>All</Trans>} />
                    <Tabs.Tab id={DocumentStatus.Pending} label={<Trans>Pending</Trans>} badge={pendingCount} />
                    <Tabs.Tab id={DocumentStatus.Approved} label={<Trans>Published</Trans>} />
                    <Tabs.Tab id={DocumentStatus.Rejected} label={<Trans>Rejected</Trans>} />
                </Tabs>

                <ButtonBar.Right>
                    <SlideToggle
                        onChange={(e) => setFilters({ ...filters, includeArchived: e.target.checked })}
                        checked={filters.includeArchived}>
                        <Trans>Show archived documents</Trans>
                    </SlideToggle>
                    <Button onClick={() => setDocumentZipDialogIsOpen(true)} icon={faFileArchive} variant="tertiary">
                        <Trans>Create a zip file</Trans>
                    </Button>
                </ButtonBar.Right>
                <DocumentsFilter
                    defaultFilter={defaultFilter}
                    filter={filters}
                    onChange={(v: ResidentialUnitDocumentSearchOptions) => setFilters(v)}
                />
            </ButtonBar>

            <AlertBanner type="info">
                <Trans>
                    If you want to upload a document for a specific unit, navigate to the unit and then go to{' '}
                    <strong>Documents</strong>. If the document should be available to multiple units, you can upload it
                    under{' '}
                    <Link to={`/projects/${projectId}/project-documents`} style={{ textDecoration: 'underline' }}>
                        <strong>Project Documents</strong>
                    </Link>
                    .
                </Trans>
            </AlertBanner>

            <UnitDocumentFilterableList projectId={projectId} filters={filters} />
            <DocumentZipDownloadDialog
                filters={{ ...filters, projectId }}
                toggle={() => setDocumentZipDialogIsOpen(false)}
                isOpen={documentZipDialogIsOpen}
            />
        </>
    );
};

export default UnitDocuments;
